import React, { useState, useEffect } from 'react';
import { dressService, Dress, CreateDressRequest, TierType, StorePrice } from '../services/dress';
import { Designer } from '../services/designer';
import { locationService, Location } from '../services/location';
import { categoryService, Category } from '../services/category';
import AuthenticatedImage from './AuthenticatedImage';
import ImageCropModal from './ImageCropModal';
import { currencySymbol } from '../utils/currency';

// Per-store price entry, keyed by locationId.
type StorePriceMap = Record<string, { purchasePrice: number; rentalPrice: number }>;

interface DressModalProps {
  dress: Dress | null;
  designers: Designer[];
  onClose: () => void;
  onSave: (savedDress?: Dress) => void;
}

const DressModal: React.FC<DressModalProps> = ({ dress, designers, onClose, onSave }) => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [selectedTiers, setSelectedTiers] = useState<TierType[]>([]);
  // Per-store prices, keyed by locationId. Each selected store has its own price
  // in its own currency (Singapore = SGD, Jakarta = IDR).
  const [storePrices, setStorePrices] = useState<StorePriceMap>({});
  const [formData, setFormData] = useState<CreateDressRequest>({
    name: '',
    description: '',
    purchasePrice: 0,
    rentalPrice: 0,
    designerId: '',
    tiers: [],
    includeInStyleMatcher: false,
    locationIds: [],
    isActive: true,
    isPopular: false,
    launchDate: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [rawImageSrc, setRawImageSrc] = useState<string | null>(null);

  useEffect(() => {
    loadLocations();
    loadCategories();
  }, []);

  useEffect(() => {
    // Set default designer when creating new dress
    if (!dress && designers.length > 0 && !formData.designerId) {
      setFormData(prev => ({ ...prev, designerId: designers[0].id }));
    }
  }, [designers, dress, formData.designerId]);

  useEffect(() => {
    if (dress) {
      const dressLocationIds = dress.locations?.map(loc => loc.id) || [];
      const dressCategoryIds = dress.dressCategories?.map(cat => cat.id) || [];
      const dressTiers = dress.tiers || [];
      setFormData({
        name: dress.name,
        description: dress.description || '',
        purchasePrice: dress.purchasePrice,
        rentalPrice: dress.rentalPrice,
        designerId: dress.designerId,
        tiers: dressTiers,
        includeInStyleMatcher: dress.includeInStyleMatcher,
        locationIds: dressLocationIds,
        isActive: dress.isActive,
        isPopular: dress.isPopular || false,
        launchDate: dress.launchDate ? dress.launchDate.split('T')[0] : '', // Format to YYYY-MM-DD
      });
      setSelectedLocationIds(dressLocationIds);
      setSelectedCategoryIds(dressCategoryIds);
      setSelectedTiers(dressTiers);

      // Hydrate per-store prices from the join data returned on each location.
      const priceMap: StorePriceMap = {};
      (dress.locations || []).forEach(loc => {
        const join = loc.DressLocation;
        priceMap[loc.id] = {
          purchasePrice: join ? Number(join.purchasePrice) || 0 : Number(dress.purchasePrice) || 0,
          rentalPrice: join ? Number(join.rentalPrice) || 0 : Number(dress.rentalPrice) || 0,
        };
      });
      setStorePrices(priceMap);

      // Set existing image preview if available
      if (dress.imageUrl) {
        setImagePreview(dress.imageUrl);
      }
    }
  }, [dress]);

  const loadLocations = async () => {
    try {
      const data = await locationService.getLocations();
      setLocations(data.filter(loc => loc.isActive));
    } catch (err) {
      console.error('Failed to load locations:', err);
    }
  };

  const loadCategories = async () => {
    try {
      const data = await categoryService.getCategories();
      setCategories(data.filter(cat => cat.isActive));
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const target = e.target as HTMLInputElement;
      setFormData(prev => ({ ...prev, [name]: target.checked }));
    } else {
      // Prices are handled per-store via handleStorePriceChange.
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const openCropModal = (file: File) => {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Only JPEG, PNG, and WebP images are allowed.');
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('Image size must be less than 10MB.');
      return;
    }

    setError('');
    const reader = new FileReader();
    reader.onloadend = () => {
      setRawImageSrc(reader.result as string);
      setShowCropModal(true);
    };
    reader.readAsDataURL(file);
  };

  const handleCropComplete = (croppedBlob: Blob) => {
    const croppedFile = new File([croppedBlob], 'dress-image.jpg', { type: 'image/jpeg' });
    setImageFile(croppedFile);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result as string);
    reader.readAsDataURL(croppedBlob);
    setShowCropModal(false);
    setRawImageSrc(null);
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    setRawImageSrc(null);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    openCropModal(file);
  };

  const handleDragEnter = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      openCropModal(files[0]);
    }
  };

  const uploadImage = async (dressId: string) => {
    if (!imageFile) return;

    setUploadingImage(true);

    try {
      const updatedDress = await dressService.uploadImage(dressId, imageFile);
      return updatedDress;
    } catch (error: any) {
      console.error('Image upload error:', error);
      throw error;
    } finally {
      setUploadingImage(false);
    }
  };

  const handleCategoryToggle = (categoryId: string) => {
    setSelectedCategoryIds(prev => {
      const newIds = prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId];
      return newIds;
    });
  };

  const handleLocationToggle = (locationId: string) => {
    setSelectedLocationIds(prev => {
      const isAdding = !prev.includes(locationId);
      const newIds = isAdding
        ? [...prev, locationId]
        : prev.filter(id => id !== locationId);
      setFormData(prevData => ({ ...prevData, locationIds: newIds }));
      // Ensure a price entry exists for a newly selected store (default 0).
      if (isAdding) {
        setStorePrices(p => p[locationId] ? p : { ...p, [locationId]: { purchasePrice: 0, rentalPrice: 0 } });
      }
      return newIds;
    });
  };

  const handleStorePriceChange = (
    locationId: string,
    field: 'purchasePrice' | 'rentalPrice',
    value: string
  ) => {
    const num = parseFloat(value) || 0;
    setStorePrices(prev => ({
      ...prev,
      [locationId]: {
        purchasePrice: prev[locationId]?.purchasePrice || 0,
        rentalPrice: prev[locationId]?.rentalPrice || 0,
        [field]: num,
      },
    }));
  };

  const handleTierToggle = (tier: TierType) => {
    setSelectedTiers(prev => {
      const newTiers = prev.includes(tier)
        ? prev.filter(t => t !== tier)
        : [...prev, tier];
      setFormData(prevData => ({ ...prevData, tiers: newTiers }));
      return newTiers;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    // Frontend validation
    if (!formData.name || !formData.designerId) {
      setError('Name and designer are required.');
      return;
    }

    // Per-store prices must be non-negative.
    const negativeStore = selectedLocationIds.find(id => {
      const sp = storePrices[id];
      return sp && (sp.purchasePrice < 0 || sp.rentalPrice < 0);
    });
    if (negativeStore) {
      setError('Store prices must be 0 or greater.');
      return;
    }

    setLoading(true);

    try {
      // Build the per-store price payload from the selected stores.
      const storePricePayload: StorePrice[] = selectedLocationIds.map(id => ({
        locationId: id,
        purchasePrice: storePrices[id]?.purchasePrice || 0,
        rentalPrice: storePrices[id]?.rentalPrice || 0,
      }));

      // First store's price keeps the legacy single-price fields in sync.
      const firstStore = storePricePayload[0];

      const dataToSend = {
        ...formData,
        purchasePrice: firstStore ? firstStore.purchasePrice : 0,
        rentalPrice: firstStore ? firstStore.rentalPrice : 0,
        storePrices: storePricePayload,
        categoryIds: selectedCategoryIds,
        locationIds: selectedLocationIds,
        tiers: selectedTiers,
        launchDate: formData.launchDate || undefined, // Convert empty string to undefined
      };
      
      let savedDress: Dress;
      
      if (dress) {
        savedDress = await dressService.updateDress(dress.id, dataToSend);
      } else {
        savedDress = await dressService.createDress(dataToSend);
      }
      
      // Upload image if a new one was selected
      if (imageFile) {
        try {
          await uploadImage(savedDress.id);
        } catch (imageError: any) {
          // Don't fail the entire operation if image upload fails
          console.error('Image upload failed:', imageError);
          setError(`Dress saved but image upload failed: ${imageError.message}`);
          // Still close the modal after a delay
          setTimeout(() => onSave(savedDress), 2000);
          return;
        }
      }
      
      onSave(savedDress);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Failed to save dress';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <div
      className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-semibold text-gray-900 mb-6">
          {dress ? 'Edit Dress' : 'Add Dress'}
        </h2>

        {error && (
          <div className="rounded-md bg-red-50 p-4 mb-4">
            <div className="text-sm text-red-700">{error}</div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Name *
                </label>
                <input
                  type="text"
                  name="name"
                  id="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>

              <div>
                <label htmlFor="designerId" className="block text-sm font-medium text-gray-700">
                  Designer *
                </label>
                <select
                  name="designerId"
                  id="designerId"
                  required
                  value={formData.designerId}
                  onChange={handleChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                >
                  <option value="">Select a designer</option>
                  {designers.map(designer => (
                    <option key={designer.id} value={designer.id}>
                      {designer.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <textarea
                name="description"
                id="description"
                rows={3}
                value={formData.description}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Dress Image
              </label>
              <div className="mt-1 md:w-1/2 md:max-w-sm">
                {imagePreview ? (
                  <div className="relative">
                    {imagePreview.startsWith('data:') || imageFile ? (
                      <img
                        src={imagePreview}
                        alt="Dress preview"
                        className="w-full h-auto object-contain rounded-md bg-gray-50"
                      />
                    ) : (
                      <AuthenticatedImage
                        src={imagePreview}
                        alt="Dress preview"
                        className="w-full h-auto object-contain rounded-md bg-gray-50"
                      />
                    )}
                    <button
                      type="button"
                      onClick={async () => {
                        if (dress && dress.imageUrl && !imageFile) {
                          // If this is an existing dress with an existing image (not a new upload)
                          if (window.confirm('Are you sure you want to delete this image?')) {
                            try {
                              setUploadingImage(true);
                              await dressService.deleteDressImage(dress.id);
                              setImagePreview(null);
                              // Update the dress object to reflect the change
                              if (dress) {
                                dress.imageUrl = undefined;
                                dress.thumbnailUrl = undefined;
                              }
                            } catch (error) {
                              console.error('Failed to delete image:', error);
                              setError('Failed to delete image. Please try again.');
                            } finally {
                              setUploadingImage(false);
                            }
                          }
                        } else {
                          // If this is a new upload that hasn't been saved yet
                          setImageFile(null);
                          setImagePreview(dress?.imageUrl || null);
                        }
                      }}
                      disabled={uploadingImage}
                      className="absolute top-2 right-2 bg-white rounded-full p-1 shadow-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {uploadingImage ? (
                        <svg className="w-5 h-5 text-gray-600 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      ) : (
                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      )}
                    </button>
                  </div>
                ) : (
                  <label
                    htmlFor="file-upload"
                    className={`flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md transition-all cursor-pointer hover:border-gray-400 ${
                      isDragging ? 'border-indigo-500 bg-indigo-50 border-solid' : 'border-gray-300'
                    }`}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                  >
                    <div className="space-y-1 text-center pointer-events-none">
                      <svg
                        className="mx-auto h-12 w-12 text-gray-400"
                        stroke="currentColor"
                        fill="none"
                        viewBox="0 0 48 48"
                        aria-hidden="true"
                      >
                        <path
                          d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <div className="flex justify-center text-sm text-gray-600">
                        <span className="font-medium text-indigo-600">Upload a file</span>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                      <p className="text-xs text-gray-500">PNG, JPG, WEBP up to 10MB</p>
                      <p className="text-xs text-gray-500 mt-1">Image will be cropped to 3:4 aspect ratio</p>
                    </div>
                    <input
                      id="file-upload"
                      name="file-upload"
                      type="file"
                      className="sr-only"
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      onChange={handleImageChange}
                    />
                  </label>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="launchDate" className="block text-sm font-medium text-gray-700">
                  Launch Date
                </label>
                <input
                  type="date"
                  name="launchDate"
                  id="launchDate"
                  value={formData.launchDate}
                  onChange={handleChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Optional: When this dress should be featured or available
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Stores &amp; Pricing
              </label>
                <div className="mt-2 space-y-2 max-h-32 overflow-y-auto border border-gray-300 rounded-md p-2">
                  {locations.length === 0 ? (
                    <p className="text-sm text-gray-500">No stores available</p>
                  ) : (
                    locations.map(location => (
                      <label key={location.id} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={selectedLocationIds.includes(location.id)}
                          onChange={() => handleLocationToggle(location.id)}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          {location.name}
                          {location.currency && (
                            <span className="ml-1 text-xs text-gray-400">({location.currency})</span>
                          )}
                        </span>
                      </label>
                    ))
                  )}
                </div>
                {selectedLocationIds.length === 0 && (
                  <p className="mt-1 text-xs text-gray-500">Select one or more stores where this dress is available</p>
                )}

                {/* Per-store price inputs — each selected store is priced in its
                    own currency (Singapore = SGD, Jakarta = IDR). */}
                {selectedLocationIds.length > 0 && (
                  <div className="mt-3 space-y-3">
                    {selectedLocationIds.map(locId => {
                      const loc = locations.find(l => l.id === locId);
                      if (!loc) return null;
                      const symbol = currencySymbol(loc.currency);
                      const sp = storePrices[locId] || { purchasePrice: 0, rentalPrice: 0 };
                      return (
                        <div key={locId} className="border border-gray-200 rounded-md p-3 bg-gray-50">
                          <p className="text-sm font-medium text-gray-700 mb-2">
                            {loc.name}
                            <span className="ml-1 text-xs text-gray-500">({loc.currency || 'SGD'})</span>
                          </p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-600">Purchase Price</label>
                              <div className="mt-1 relative">
                                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 text-sm pointer-events-none">{symbol}</span>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={sp.purchasePrice}
                                  onChange={(e) => handleStorePriceChange(locId, 'purchasePrice', e.target.value)}
                                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                />
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600">Rental Price</label>
                              <div className="mt-1 relative">
                                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 text-sm pointer-events-none">{symbol}</span>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={sp.rentalPrice}
                                  onChange={(e) => handleStorePriceChange(locId, 'rentalPrice', e.target.value)}
                                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Categories
              </label>
              <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto border border-gray-300 rounded-md p-3">
                {categories.length === 0 ? (
                  <p className="text-sm text-gray-500">No categories available</p>
                ) : (
                  categories.map(category => (
                    <label key={category.id} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedCategoryIds.includes(category.id)}
                        onChange={() => handleCategoryToggle(category.id)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">{category.displayName}</span>
                    </label>
                  ))
                )}
              </div>
              {selectedCategoryIds.length === 0 && (
                <p className="mt-1 text-xs text-gray-500">Select one or more categories that describe this dress</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tiers (Select which customer tiers can view this dress)
              </label>
              <div className="space-y-2 border border-gray-300 rounded-md p-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedTiers.includes('new')}
                    onChange={() => handleTierToggle('new')}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-2">New</span>
                    Visible to new customers
                  </span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedTiers.includes('paid_deposit')}
                    onChange={() => handleTierToggle('paid_deposit')}
                    className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 mr-2">Paid Deposit</span>
                    Visible to paid deposit customers
                  </span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedTiers.includes('exclusive')}
                    onChange={() => handleTierToggle('exclusive')}
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 mr-2">Exclusive</span>
                    Visible to exclusive customers
                  </span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedTiers.includes('partner')}
                    onChange={() => handleTierToggle('partner')}
                    className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-800 mr-2">Partner</span>
                    Visible to partner accounts (partners see only partner-tagged dresses)
                  </span>
                </label>
              </div>
              {selectedTiers.length === 0 && (
                <p className="mt-1 text-xs text-gray-500">
                  Select at least one tier to make this dress visible to customers
                </p>
              )}
            </div>

            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="includeInStyleMatcher"
                  checked={formData.includeInStyleMatcher}
                  onChange={handleChange}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm font-medium text-gray-700">
                  Include in Style Matcher
                </span>
              </label>
              <p className="mt-1 text-xs text-gray-500 ml-6">
                When enabled, this dress will appear in the Style Matcher recommendations
              </p>
            </div>

            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm font-medium text-gray-700">
                  Active
                </span>
              </label>
              <p className="mt-1 text-xs text-gray-500 ml-6">
                When active, this dress will be visible in the customer catalog
              </p>
            </div>

            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="isPopular"
                  checked={formData.isPopular}
                  onChange={handleChange}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm font-medium text-gray-700">
                  Popular
                </span>
              </label>
              <p className="mt-1 text-xs text-gray-500 ml-6">
                Mark this dress as popular to feature it prominently
              </p>
            </div>
          </div>

          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (uploadingImage ? 'Uploading image...' : 'Saving...') : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>

      {/* Rendered as a sibling of the backdrop (not a child) so clicks inside
          the crop modal don't bubble up to the backdrop's onClick={onClose}
          and tear down the whole dress form. */}
      {showCropModal && rawImageSrc && (
        <ImageCropModal
          imageSrc={rawImageSrc}
          aspectRatio={3 / 4}
          title="Crop Dress Image"
          outputWidth={1800}
          outputHeight={2400}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
        />
      )}
    </>
  );
};

export default DressModal;